import { useRef, useMemo, useEffect } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

export function ThreeScene() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<THREE.Scene>();
  const cameraRef = useRef<THREE.PerspectiveCamera>();
  const rendererRef = useRef<THREE.WebGLRenderer>();
  const controlsRef = useRef<OrbitControls>();
  const animationId = useRef<number>();
  const particlesRef = useRef<THREE.Points>();
  const timeRef = useRef(0);

  const vertexShader = useMemo(() => `
    uniform float uTime;
    uniform float uProgress;
    attribute float aSize;
    attribute vec3 aColor;
    attribute float aPhase;
    varying vec3 vColor;
    varying float vAlpha;
    
    void main() {
      vec3 pos = position;
      float phase = aPhase + uTime * 0.5;
      
      pos.x += sin(phase) * 0.5;
      pos.y += cos(phase * 0.7) * 0.5;
      pos.z += sin(phase * 1.3) * 0.3;
      
      float pulse = sin(uTime * 2.0 + aPhase * 10.0) * 0.5 + 0.5;
      float size = aSize * (0.5 + pulse * 0.5) * (1.0 + uProgress * 2.0);
      
      vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
      gl_PointSize = size * (300.0 / -mvPosition.z);
      gl_Position = projectionMatrix * mvPosition;
      
      vColor = aColor;
      vAlpha = 0.6 + pulse * 0.4;
    }
  `, []);

  const fragmentShader = useMemo(() => `
    uniform float uTime;
    varying vec3 vColor;
    varying float vAlpha;
    
    void main() {
      vec2 center = gl_PointCoord - 0.5;
      float dist = length(center);
      
      if (dist > 0.5) discard;
      
      float alpha = smoothstep(0.5, 0.0, dist) * vAlpha;
      vec3 color = vColor;
      
      float glow = smoothstep(0.3, 0.0, dist);
      color += vec3(0.0, 1.0, 0.5) * glow * 0.5;
      
      gl_FragColor = vec4(color, alpha);
    }
  `, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const scene = new THREE.Scene();
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.set(0, 5, 15);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x0a0a0f, 1);
    rendererRef.current = renderer;

    const controls = new OrbitControls(camera, canvas);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.enablePan = false;
    controls.minDistance = 8;
    controls.maxDistance = 30;
    controls.maxPolarAngle = Math.PI / 2 - 0.1;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.2;
    controlsRef.current = controls;

    const particleCount = 3000;
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    const sizes = new Float32Array(particleCount);
    const colors = new Float32Array(particleCount * 3);
    const phases = new Float32Array(particleCount);

    const color1 = new THREE.Color(0x00ff88);
    const color2 = new THREE.Color(0x00d4ff);
    const color3 = new THREE.Color(0xff6b35);
    const color4 = new THREE.Color(0xffd700);

    for (let i = 0; i < particleCount; i++) {
      const radius = 5 + Math.random() * 10;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);

      positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta) + 2;
      positions[i * 3 + 2] = radius * Math.cos(phi);

      sizes[i] = 0.5 + Math.random() * 1.5;
      phases[i] = Math.random() * Math.PI * 2;

      const colorChoice = Math.random();
      let color: THREE.Color;
      if (colorChoice < 0.3) color = color1;
      else if (colorChoice < 0.55) color = color2;
      else if (colorChoice < 0.8) color = color3;
      else color = color4;

      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1));
    geometry.setAttribute('aColor', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1));

    const material = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        uTime: { value: 0 },
        uProgress: { value: 0 },
      },
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      vertexColors: true,
    });

    const particles = new THREE.Points(geometry, material);
    particlesRef.current = particles;
    scene.add(particles);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);

    const pointLight1 = new THREE.PointLight(0x00ff88, 1, 50);
    pointLight1.position.set(10, 10, 10);
    scene.add(pointLight1);

    const pointLight2 = new THREE.PointLight(0x00d4ff, 1, 50);
    pointLight2.position.set(-10, 5, -10);
    scene.add(pointLight2);

    const pointLight3 = new THREE.PointLight(0xff6b35, 1, 50);
    pointLight3.position.set(0, -5, 10);
    scene.add(pointLight3);

    const animate = () => {
      animationId.current = requestAnimationFrame(animate);
      timeRef.current += 0.016;

      if (material.uniforms) {
        material.uniforms.uTime.value = timeRef.current;
      }

      if (particlesRef.current) {
        particlesRef.current.rotation.y += 0.0005;
        particlesRef.current.rotation.x += 0.0002;
      }

      controlsRef.current?.update();
      rendererRef.current?.render(scene, camera);
    };

    animate();

    const handleResize = () => {
      if (!cameraRef.current || !rendererRef.current) return;
      cameraRef.current.aspect = window.innerWidth / window.innerHeight;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationId.current) cancelAnimationFrame(animationId.current);
      geometry.dispose();
      material.dispose();
      rendererRef.current?.dispose();
      controlsRef.current?.dispose();
    };
  }, [vertexShader, fragmentShader]);

  return (
    <canvas
      ref={canvasRef}
      className="three-canvas"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        pointerEvents: 'none',
      }}
      aria-hidden="true"
    />
  );
}